import React from 'react'
import { useNavigate } from 'react-router-dom';
import CategoryFilter from '../components/CategoryFilter.jsx';
import ProductCard from '../components/ProductCard.jsx';
import Pagination from '../components/Pagination.jsx';

export default function SavedItemsPage({ user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, onViewProduct, onLogout, onToggleSave, savedItems: savedItemsProp }) {
    const navigate = useNavigate();
    const [apiSavedItems, setApiSavedItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    const [categories, setCategories] = React.useState([]);
    const [stickyHeight, setStickyHeight] = React.useState(0);

    // Anonymous users use the client-side savedItems prop (sessionStorage).
    // Logged-in users fetch from the server API.
    const savedItems = user ? apiSavedItems : (savedItemsProp || []);

    React.useEffect(() => {
        // Fetch categories for the header
        fetch('/api/categories.php')
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => console.error("Failed to fetch categories", err));

        const fetchSavedItems = async () => {
            try {
                setLoading(true);
                if (user) {
                    // Logged-in: fetch from server API.
                    const response = await fetch('/api/get_saved_items.php', { credentials: 'include' });
                    if (!response.ok) throw new Error('Failed to fetch saved items.');
                    const data = await response.json();
                    setApiSavedItems(Array.isArray(data) ? data : []);
                }
                // Anonymous: savedItems prop (client-side sessionStorage) is already populated by parent.
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSavedItems();
    }, [user]);

    if (loading) {
        return <div className="text-center py-20"><i className="fas fa-spinner fa-spin text-4xl text-blue-400"></i></div>;
    }

    if (error) {
        return <div className="text-center py-20 text-red-500">Error: {error}</div>;
    }

    return (
        <div data-name="saved-items-page" className="min-h-screen">
            <CategoryFilter
                categories={categories}
                selectedCategory="all"
                setSelectedCategory={(cat) => navigate(`/${cat === 'all' ? 'equipment' : cat}`)}
                searchTerm=""
                setSearchTerm={(term) => navigate(`/search?search=${encodeURIComponent(term)}`)}
                pageTitle="My Saved Items"
                pageSubtitle="Your favorite products, all in one place."
                user={user}
                onShowLogin={onShowLogin}
                onLogout={onLogout}
                 basketCount={basket ? basket.reduce((acc, item) => acc + item.quantity, 0) : 0}
                 savedItems={savedItems}
                 totalItems={savedItems.length}
                onStickyHeightChange={setStickyHeight}
            />

            <div className="container mx-auto px-4 pb-8" style={{ paddingTop: `${stickyHeight}px` }}>
                {/* Header Section - Modern Industrial Style */}
                <div className="mb-10 text-center pt-8 animate-fade-in">
                    <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase leading-tight">
                        My Saved Items
                    </h1>
                    <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-xs mt-3">Your favorite products, all in one place.</p>
                </div>

                {savedItems.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-xl shadow-lg">
                        <i className="far fa-heart text-6xl text-gray-300 mb-4"></i>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">You have no saved items.</h2>
                        <p className="text-gray-600">Click the heart icon on a product to save it for later.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {savedItems.map(product => (
                            <ProductCard
                                key={`${product.listingType}-${product.id}`}
                                product={product}
                                badgeType="standard"
                                onViewDetails={() => onViewProduct({ ...product, badgeType: 'standard' })}
                                user={user}
                                basket={basket}
                                onAddToBasket={onAddToBasket}
                                onUpdateBasketQuantity={onUpdateBasketQuantity}
                                onRemoveFromBasket={onRemoveFromBasket}
                                onShowLogin={onShowLogin}
                                savedItems={savedItems}
                                onToggleSave={onToggleSave}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
